/**
 * NotFound Page Component
 *
 * A 404 error page with a modern design that provides helpful navigation options
 * and maintains the application's visual consistency.
 */

import { Button } from "../../base/button";
import { Home, ArrowLeft, Search, HelpCircle } from "lucide-react";
import { cn } from "../../../lib/utils";

export interface NotFoundProps {
  /** Custom error message */
  message?: string;
  /** Custom description */
  description?: string;
  /** Whether to show the back button */
  showBackButton?: boolean;
  /** Custom CSS classes */
  className?: string;
  /** Whether to show additional help options */
  showHelpOptions?: boolean;
  /** Custom Link component for navigation */
  Link?: React.ComponentType<{
    to: string;
    className?: string;
    children: React.ReactNode;
    onClick?: () => void;
  }>;
  /** Navigation handlers */
  onGoHome?: () => void;
}

/**
 * NotFound Page Component
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <NotFound
 *       message="Page not found"
 *       description="The page you're looking for doesn't exist."
 *       showBackButton={true}
 *       showHelpOptions={true}
 *     />
 *   );
 * }
 * ```
 */
export function NotFound({
  message = "Page not found",
  description = "The page you're looking for doesn't exist or has been moved.",
  showBackButton = true,
  showHelpOptions = true,
  className,
  Link,
  onGoHome,
}: NotFoundProps) {
  // Default Link component that uses regular anchor tags
  const DefaultLink =
    Link ||
    (({
      to,
      className,
      children,
      onClick,
    }: {
      to: string;
      className?: string;
      children: React.ReactNode;
      onClick?: () => void;
    }) => (
      <a href={to} className={className} onClick={onClick}>
        {children}
      </a>
    ));
  return (
    <div
      className={cn(
        "min-h-[60vh] flex items-center justify-center px-4 py-8",
        className,
      )}
    >
      <div className="max-w-md w-full text-center space-y-6">
        {/* 404 Number */}
        <div className="space-y-2">
          <h1 className="text-8xl font-bold text-primary/20 select-none">
            404
          </h1>
          <div className="w-16 h-1 bg-primary/20 rounded-full mx-auto"></div>
        </div>

        {/* Error Message */}
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">{message}</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {description}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild className="flex-1 sm:flex-none">
            <DefaultLink to="/" onClick={onGoHome}>
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </DefaultLink>
          </Button>

          {showBackButton && (
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="flex-1 sm:flex-none"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          )}
        </div>

        {/* Help Options */}
        {showHelpOptions && (
          <div className="pt-6 border-t border-border/50">
            <p className="text-xs text-muted-foreground mb-4">
              Need help? Try these options:
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <DefaultLink to="/dashboard">
                  <Search className="mr-2 h-3 w-3" />
                  Browse Dashboard
                </DefaultLink>
              </Button>
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <DefaultLink to="/customers">
                  <HelpCircle className="mr-2 h-3 w-3" />
                  View Customers
                </DefaultLink>
              </Button>
            </div>
          </div>
        )}

        {/* Decorative Elements */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/5 rounded-full blur-xl"></div>
          <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/5 rounded-full blur-xl"></div>
        </div>
      </div>
    </div>
  );
}

export default NotFound;
